import { ButtonType, CheckBoxWithLabelWidget, DropdownButtonType, List, ListItem, SingleSelectDropdown, TextFieldWidget,  } from '@tableau/widgets';
import React from 'react';

import { SignInLogo } from '~src/app/spaces/Login/SignInLogo';
import { Messages } from '~src/codegen/Localize';
import { Helpers } from '~src/ts/helpers/Helpers';
import { IAllowedAuthenticationMode } from '~src/ts/ServerApi/types/IDataConnectionAuthInfo';

import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { ConnectionAuthenticationProps, IConnectionProperty } from './AuthenticateConnection';

export function AuthenticationComplete() {
    return (
        <div className="tb-connection-auth" data-tb-test-id="tb-connection-auth-complete">
            <div className="tb-connection-auth-connected">{Messages.authConnectionFinished()}</div>
            <div className="tb-connection-auth-prompt">{Messages.authConnectionCloseMe()}</div>
        </div>
    );
}

export interface AuthenticationIncompleteProps {
    readonly connectionAuthenticationProps: ConnectionAuthenticationProps;
    readonly attemptAuth: () => void;
    readonly busy: boolean;
    readonly authenticationRememberPassword: boolean;
    readonly errorMessageKey: string;
    readonly selectedConnectionAuthMode: string;
    readonly authenticationUsername: string;
    readonly authenticationPassword: string;
    readonly setSelectedConnectionAuthMode: React.Dispatch<React.SetStateAction<string>>;
    readonly setAuthenticationUsername: React.Dispatch<React.SetStateAction<string>>;
    readonly setAuthenticationPassword: React.Dispatch<React.SetStateAction<string>>;
    readonly setAuthenticationRememberPassword: React.Dispatch<React.SetStateAction<boolean>>;
    readonly usernameRef: React.MutableRefObject<HTMLInputElement>;
    readonly passwordRef: React.MutableRefObject<HTMLInputElement>;
}

export function AuthenticationIncomplete(props: AuthenticationIncompleteProps) {

    const connectionProperties: IConnectionProperty[] = getConnectionProperties(props.connectionAuthenticationProps);
    const authModeOptions: IAllowedAuthenticationMode[] = getAuthModeOptions(props.connectionAuthenticationProps);
    const rememberPasswordsForever: boolean = props.connectionAuthenticationProps.authInfo.rememberPasswordsForever;
    const focusOnPassword: boolean = !!props.connectionAuthenticationProps.authInfo.defaultUsername;
    const isPreview: boolean = Helpers.BooleanQueryParamRegex.test(props.connectionAuthenticationProps.queryParams.isPreview);
    const enableRememberPassword: boolean = props.connectionAuthenticationProps.authInfo.enableRememberPassword;
    const authOptionsList: ListItem[] = getAuthOptionsList(authModeOptions);

    React.useEffect(
      () => {
        focusOnPassword ? props.passwordRef.current.focus() : props.usernameRef.current.focus();
      },
      // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [focusOnPassword]
    );

    return (
        <div className="tb-connection-auth" data-tb-test-id="tb-connection-auth-incomplete">
            <div className="tb-login-form-container" data-tb-test-id="tb-login-form-container">
                <form onSubmit={(event) => {
                    event.preventDefault();
                    props.attemptAuth();
                }}>
                    <SignInLogo />
                    <div className="tb-connection-auth-prompt" data-tb-test-id="tb-connection-auth-incomplete-prompt">{Messages.authConnectionPrompt()}</div>
                    {authOptionsList && authOptionsList.length > 1 &&
                        <div className="tb-login-text-box" data-tb-test-id="tb-authentication-drop-down">
                            <SingleSelectDropdown
                                data-tb-test-id='tb-test-dropdown'
                                className="tb-login-text-box"
                                label={Messages.authentication()}
                                type={DropdownButtonType.Outline}
                                testId='auth-connection-auth-mode-select'
                                value={props.selectedConnectionAuthMode}
                                onChange={props.setSelectedConnectionAuthMode}
                                list={<List items={authOptionsList} />}
                            >
                                {getNameForAuthModeValue(props.selectedConnectionAuthMode, authModeOptions)}
                            </SingleSelectDropdown>
                        </div>
                    }
                    <div className="tb-login-text-box tb-margin-top-wide" data-tb-test-id="tb-username-text-box">
                        <TextFieldWidget
                            data-tb-test-id='textbox-username-auth'
                            label={Messages.username()}
                            kind='line'
                            testId="textbox-username"
                            onChange={e => props.setAuthenticationUsername(e.target.value)}
                            ref={props.usernameRef}
                            value={props.authenticationUsername}
                        />
                    </div>
                    <div className="tb-login-text-box tb-margin-top-wide">
                        <TextFieldWidget
                            data-tb-test-id='textbox-password-auth'
                            label={Messages.password()}
                            kind='line'
                            testId="textbox-password"
                            onChange={e => props.setAuthenticationPassword(e.target.value)}
                            ref={props.passwordRef}
                            type='password'
                            value={props.authenticationPassword}
                        />
                    </div>
                    <div className="tb-padded tb-login-text-box">
                        {!isPreview &&
                            <div className="tb-padded" >
                                <CheckBoxWithLabelWidget
                                    data-tb-test-id='checkbox-rememberpassword'
                                    handleChange={(checked: boolean) => {
                                        props.setAuthenticationRememberPassword(checked);
                                    }}
                                    checked={props.authenticationRememberPassword}
                                    disabled={!enableRememberPassword}
                                    testId="rememberPassword"
                                    label={rememberPasswordsForever ? Messages.authConnectionRememberPassword() : Messages.authConnectionRememberPasswordUntilSignOut()}
                                />
                            </div>
                        }
                    </div>
                    <div className="tb-padded tb-margin-top-wide">
                        <ButtonWithSpinner
                            buttonType={ButtonType.Primary}
                            type="submit"
                            disabled={props.busy ||
                                !props.authenticationUsername ||
                                props.authenticationUsername.trim() === '' ||
                                !props.authenticationPassword ||
                                props.authenticationPassword.trim() === ''}
                            testId="button-signin"
                            data-tb-test-id='authenticate-button-signin'
                            showSpinner={props.busy}
                            title={Messages.signIn()}
                        />

                    </div>
                    <div className="tb-auth-properties">
                        <div className="tb-connection-property"  data-tb-test-id="connection-properties-list">
                            {connectionProperties.map((connectionProperty, index) => (
                                <div key={index}>
                                    <span className="tb-connection-property-label">
                                        {connectionProperty.message}
                                    </span>
                                    <span className="tb-connection-property-value" data-tb-test-id={connectionProperty.tbTestId}>
                                        {connectionProperty.value}
                                    </span>
                                </div>
                            ))
                            }

                        </div>
                    </div>
                    {props.errorMessageKey !== '' &&
                        <div
                            className="tb-padded tb-connection-auth-error"
                            data-tb-test-id='message-failure'
                        >
                            {props.errorMessageKey}
                        </div>
                    }
                </form>
            </div>
        </div>
    );
}


function getAuthModeOptions(props: ConnectionAuthenticationProps): IAllowedAuthenticationMode[] {
    // If there more than 1 allowedAuthenticationModes then list the options for the user.
    if (props.authInfo.allowedAuthenticationModes && props.authInfo.allowedAuthenticationModes.length > 1) {
        return props.authInfo.allowedAuthenticationModes;
    }
}

function getConnectionProperties(props: ConnectionAuthenticationProps): IConnectionProperty[] {
    // If there is 1 allowedAuthenticationMode, then try to get the friendly name if it exists.
    let authModeFriendlyName: string; // Localized authentication mode for the current authentication mode
    if (props.authInfo.allowedAuthenticationModes && props.authInfo.allowedAuthenticationModes.length === 1) {
        authModeFriendlyName = props.authInfo.allowedAuthenticationModes[0].modeFriendlyName;
    }

    const connectionproperties = [];
    if (props.authInfo.connectionType) connectionproperties.push({ message: Messages.authConnectionConnectionType(), value: props.authInfo.connectionTypeDisplayName, tbTestId: 'auth-connection-type' });
    if (authModeFriendlyName) connectionproperties.push({ message: Messages.authConnectionAuthentication(), value: authModeFriendlyName, tbTestId: 'auth-connection-fixed-auth-mode' });
    if (props.authInfo.databaseServer) connectionproperties.push({ message: Messages.authConnectionServer(), value: props.authInfo.databaseServer, tbTestId: 'auth-connection-server' });
    if (props.authInfo.databaseName) connectionproperties.push({ message: Messages.authConnectionDatabase(), value: props.authInfo.databaseName, tbTestId: 'auth-connection-database' });
    if (props.authInfo.databaseSchema) connectionproperties.push({ message: Messages.authConnectionSchema(), value: props.authInfo.databaseSchema, tbTestId: 'auth-connection-schema' });
    if (props.authInfo.databaseFilename) connectionproperties.push({ message: Messages.authConnectionFilename(), value: props.authInfo.databaseFilename, tbTestId: 'auth-connection-filename' });
    if (props.authInfo.samlIdP) connectionproperties.push({ message: Messages.authConnectionSAMLIdP(), value: props.authInfo.samlIdP, tbTestId: 'auth-connection-saml-idp' });

    return connectionproperties;
}

function getAuthOptionsList(authModeOptions: IAllowedAuthenticationMode[]): ListItem[] {
    const items: ListItem[] = [];
    if (authModeOptions) {
        authModeOptions.map((options) => {
            items.push({ value: options.mode, content: options.modeFriendlyName });
        });
    }
    return items;
}

function getNameForAuthModeValue(value: string, authModeOptions: IAllowedAuthenticationMode[]): string {
    return authModeOptions.find(authModeOption => authModeOption.mode === value).modeFriendlyName;
}
